import { ChangeDetectorRef, Component, OnInit, Input, Output, EventEmitter, OnDestroy, OnChanges, SimpleChanges, inject } from '@angular/core';
import { ExperienceUnit, ExperienceUnitGroupListItem, ThemeSpec, UnitGroupType } from '../models';
import { lastValueFrom, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import LZString from 'lz-string';
import { ExperienceService } from '../../services/experience.service';
import { PreSavedParamsService } from 'src/app/modules/shared/services/pre-saved-params.service';
import { AccountContextService } from 'src/app/modules/shared/services/account-context.service';

@Component({
  selector: 'app-unit-preview',
  templateUrl: './unit-preview.component.html',
  styleUrls: ['./unit-preview.component.scss'],
  standalone: false
})
export class UnitPreviewComponent implements OnInit, OnDestroy, OnChanges {
  private readonly experienceService = inject(ExperienceService);
  private readonly preSavedParamsService = inject(PreSavedParamsService);
  private readonly accountContext = inject(AccountContextService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);


  @Input() unit: ExperienceUnit;
  @Input() inAccount = true;
  @Input() maxHeight = 677;
  @Input() hideTopButtons = false;
  @Input() extraQueryParams: string | null = null;
  @Input() previewHeightOverride: number | null = null;
  @Output() previewHtml = new EventEmitter<string>();
  previewType: 'desktop' | 'mobile' = 'desktop';
  previewHeight: number;
  urlPreviewSubject = new Subject<string>();
  previewUrlStr: string;
  private accountSettingsCompressed: string;
  showPreviewForm = false;
  iframeVisible = true;
  pageUrl: string;
  compressedParams: string;
  allGroups: ExperienceUnitGroupListItem[] = [];

  async ngOnInit(): Promise<void> {
    if (this.inAccount) {
      const groupsRes = await lastValueFrom(this.experienceService.getUnitGroups());
      this.allGroups = groupsRes.items;
    }

    window.addEventListener('message', e => this.handleIframeMessage(e));

    const compressedUrl = this.getCompressedPreviewUrl();
    this.accountSettingsCompressed = this.getCompressedAccountSettings();
    this.previewUrlStr = this.buildPreviewUrl(compressedUrl);
    this.compressedParams = compressedUrl;
    this.changeDetectorRef.markForCheck();

    this.urlPreviewSubject.pipe(
      distinctUntilChanged(),
      debounceTime(800),
    )
      .subscribe(newQueryString => {
        this.previewUrlStr = this.buildPreviewUrl(newQueryString);
        this.compressedParams = newQueryString;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['extraQueryParams'] && this.compressedParams) {
      this.previewUrlStr = this.buildPreviewUrl(this.compressedParams);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.handleIframeMessage);
  }

  get previewUrlDetector(): string {
    const compressedUrl = this.getCompressedPreviewUrl();
    this.urlPreviewSubject.next(compressedUrl);
    return compressedUrl;
  }

  getCompressedPreviewUrl(): string {
    const group = this.unit.groupId ? this.allGroups.find(g => g.id === this.unit.groupId) : null;
    const groupConfig = group?.groupType === UnitGroupType.AutoEmbed ? {
      selector: group.selector,
      placementRules: group.placementRules
    } : null;
    // Campaign Name is metadata and should not trigger preview refresh.
    const previewModelUnit = { ...this.unit, name: '' };

    const previewObj = {
      id: this.unit.unitId,
      viewModelId: this.unit.guid,
      params: {
        backgroundColor: this.unit.backgroundColor,
        textColor: this.unit.textColor,
        linkColor: this.unit.linkColor,
        buttonBackgroundColor: this.unit.buttonBackgroundColor,
        buttonTextColor: this.unit.buttonTextColor,
        secondaryTextColor: this.unit.secondaryTextColor,
        successTextColor: this.unit.successTextColor,
        carouselIndicatorColor: this.unit.recommendCarouselIndicatorColor,
        titleSize: this.unit.titleSize,
        subTitleSize: this.unit.subTitleSize,
        successTextSize: this.unit.successTextSize,
        buttonTextSize: this.unit.buttonTextSize,
        inputBackgroundColor: this.unit.inputBackgroundColor,
        inputTextColor: this.unit.inputTextColor,
        inputBorderColor: this.unit.inputBorderColor
      } as ThemeSpec,
      css: this.unit.css?.replace(/{{ID}}/g, this.unit.guid).replace(/{{VISIBILITY}}/g, this.unit.visibilityType),
      model: {
        ...previewModelUnit,
        txt: this.unit.subTitle,
        limit: this.unit.recommendationsLimit,
        fields: this.unit.formFields,
        logoRelativeUrl: this.unit.imgUrl,
        groupId: group?.name,
        groupConfig,
        recommendIsCarousel: this.unit.recommendIsCarousel,
        displayLimit: this.unit.recommendCarouselDisplayLimit,
        carouselShowIndicator: this.unit.recommendCarouselShowIndicator,
        urlFilter: null,
        showPageSummary: this.unit.showPageSummary,
        showChat: this.unit.showChat,
        showCtaButton: !!this.unit.ctaButtonConfig,
        placeholderText: this.unit.placeholderText,
        disclamerText: this.unit.disclamerText,
        guidingQuestions: this.unit.guidingQuestions,
        ctaButtonConfig: this.unit.ctaButtonConfig
      }
    };

    const compressed = LZString.compressToEncodedURIComponent(JSON.stringify(previewObj));
    return compressed;
  }

  getCompressedAccountSettings(): string {
    const accObj = {
      accessibilitySupport: this.accountContext.supports('accessibilitySupport'),
    };
    const compressed = LZString.compressToEncodedURIComponent(JSON.stringify(accObj));
    return compressed;
  }

  private buildPreviewUrl(compressedParams: string): string {
    const extra = this.previewType === 'desktop' && this.extraQueryParams ? `&${this.extraQueryParams}` : '';
    const mobileParam = this.previewType === 'mobile' ? '&trd_mbprv=1' : '';
    return `${environment.engagmentPreviewBaseUrl}/preview-unit.html?layoutType=${this.unit.uiType}&p=${compressedParams}&as=${this.accountSettingsCompressed}${mobileParam}${extra}`;
  }

  getPreviewHeight(currentHeight: number, currentWidth: number): number {
    if (this.previewHeightOverride != null && this.previewType === 'desktop') {
      return this.previewHeightOverride;
    }
    if (this.previewType === 'desktop') {
      return this.previewHeight ? this.previewHeight : this.previewHeight = Math.max(Math.min(currentHeight, window.screen.availHeight), this.maxHeight);
    } else {
      return 667;
    }
  }
  getPreviewWidth(currentHeight: number, currentWidth: number): string {
    if (this.previewType === 'desktop') { return '100%'; }
    return '375';
  }

  onPreviewTypeClick(type: 'desktop' | 'mobile'): void {
    this.previewType = type;
    if (this.compressedParams) {
      this.previewUrlStr = this.buildPreviewUrl(this.compressedParams);
    }
    this.reloadPreviewIframe();
  }

  reloadPreviewIframe(): void {
    this.iframeVisible = false;
    setTimeout(() => this.iframeVisible = true, 0);
  }

  async openPreviewPage(): Promise<void> {
    const paramsId = await this.preSavedParamsService.create('unit', this.compressedParams);
    const separator = this.pageUrl.indexOf('?') < 0 ? '?' : '&';
    const queryParams = this.preSavedParamsService.getQueryParameters(paramsId, 'unit');
    const url = `${this.pageUrl}${separator}${queryParams}&td=demo`;
    this.showPreviewForm = false;
    window.open(url, '_blank');
  }

  handleIframeMessage(event: MessageEvent): void {
    const { type, html } = event.data || {};
    if (type === 'unitPreviewHtml' && typeof html === 'string') {
      this.previewHtml.emit(html);
    }
  };
}
